/** Part catalogue and keyboard shortcuts. DOM/CSS owns palette layout and hit testing. */
import { objectsByCategory, type ObjectDef } from '../game/objects'
import type { PaletteCategory } from '../game/types'
import { TERRAIN_TOOLS, type Tool } from './tools'

const BY_CATEGORY = objectsByCategory()

/** One selectable palette cell. */
export interface PaletteEntry {
  /** Stable identity: `t:<terrainId>` or `o:<objectId>`. */
  key: string
  kind: 'terrain' | 'object'
  /** Position within its category. */
  index: number
  /** Number-key shortcut, 1-9, or 0 when past the ninth cell. */
  slot: number
  /** Label, shown when nothing is hovered. */
  name: string
  /** Two character code drawn on the cell. */
  code: string
  /** Contextual help displayed by the HTML palette. */
  hint: string
  /** Terrain id for terrain cells, `Terrain.EMPTY` otherwise. */
  terrain: number
  /** Tool a terrain cell selects. */
  toolId: string | null
  objectId: string | null
  def: ObjectDef | null
  color: string
}

/**
 * A two character code for an object, derived from its name so the palette
 * needs no hand-maintained lookup table: the first two word-initial letters,
 * keeping leading symbols because `? Block` should read `?B`, not `BL`.
 */
export function codeFor(name: string): string {
  const words = name.split(/[^A-Za-z0-9?+/!]+/).filter((w) => w.length > 0)
  const a = words[0] ?? '?'
  const b = words[1]?.[0] ?? a[1] ?? '?'
  return (a[0] ?? '?').toUpperCase() + (b ?? '?').toUpperCase()
}

function terrainEntry(tool: Tool, index: number): PaletteEntry {
  return {
    key: `t:${tool.cursorTile}`,
    kind: 'terrain',
    index,
    slot: index < 9 ? index + 1 : 0,
    name: tool.name,
    code: tool.code,
    hint: tool.hint,
    terrain: tool.cursorTile,
    toolId: tool.id,
    objectId: null,
    def: null,
    color: tool.color,
  }
}

function objectEntry(def: ObjectDef, index: number): PaletteEntry {
  return {
    key: `o:${def.id}`,
    kind: 'object',
    index,
    slot: index < 9 ? index + 1 : 0,
    name: def.name,
    code: codeFor(def.name),
    hint: def.hint ?? '',
    terrain: 0,
    toolId: null,
    objectId: def.id,
    def,
    color: CATEGORY_COLORS[def.category],
  }
}

/** One accent colour per palette category, used for cell fills. */
export const CATEGORY_COLORS: Record<PaletteCategory, string> = {
  terrain: '#6b8cff',
  blocks: '#7c7c7c',
  pipes: '#00a800',
  platforms: '#a44200',
  hazards: '#d82800',
  enemies: '#e45c10',
  items: '#fcd800',
  scenery: '#3cbcfc',
}

/** Every entry in a category, in palette order. Terrain first. */
export function paletteEntries(category: PaletteCategory): PaletteEntry[] {
  if (category === 'terrain') return TERRAIN_TOOLS.map(terrainEntry)
  return (BY_CATEGORY.get(category) ?? []).map(objectEntry)
}

/** Number keys select the first nine catalogue entries, regardless of DOM scrolling. */
export function entryForSlot(
  entries: PaletteEntry[],
  slot: number,
): PaletteEntry | null {
  if (slot < 1 || slot > 9) return null
  return entries[slot - 1] ?? null
}
